import { createEventModel } from '$tablet-app/src/app/models';
import { showErrorToast } from '$tablet-app/src/app/toasts';
import { useCurrentUser } from '$tablet-app/src/app/users';
import { useCreateCalendarItem } from '@akin-home/calendar/combined';
import React, { useCallback, useEffect } from 'react';

import { sendBackgroundMessage } from '$tablet-app/src/app/chat/functions';
import {
  useSaveCommand,
  useUpdateScreenChatContext,
} from '$tablet-app/src/app/chat/hooks';
import { intent_labels } from '$tablet-app/src/app/chat/intent_labels';
import { DeveloperScreenInfo } from '$tablet-app/src/app/developer/components/DeveloperScreenInfo/DeveloperScreenInfo';
import { JSONText } from '$tablet-app/src/app/developer/components/JSONText';
import { RootStackScreenProps } from '$tablet-app/src/app/navigation/RootStackNavigator/RootStackNavigator';
import {
  getDefaultReminders
} from '@akin-home/calendar-reminders';
import { showInfoToast } from '@akin-home/toast/native';
import { afterCreateCalendarItem } from '../../functions/afterCreateCalendarItem';
import { useUpdateCalendarItemFormListener } from '../../hooks';
import { validateCalendarItemFormSubmission } from '../CalendarItemForm';
import CalendarItemForm from '../CalendarItemForm/CalendarItemForm';
import { useCalendarItemForm } from '../CalendarItemForm/useCalendarItemForm';

const screen_context = {
  canSave: true,

  fields: {
    title: {
      type: 'text',
    },
    emoji: {
      type: 'emoji',
    },
    start_date: {
      type: 'date_text',
    },
    start_time: {
      type: 'hh:mm or hh:mm:ss',
    },
    end_date: {
      type: 'date_text',
    },
    end_time: {
      type: 'hh:mm or hh:mm:ss',
    },
    notes: {
      type: 'text',
    },
    color: {
      type: 'color',
    },
  },
};

const parameters = {
  defaults: {
    title: 'text',
    emoji: 'text',
    start: 'date/time text',
    end: 'date/time text',
    color: 'color code',
  },
};

/** Screen Component for creating calendar items (events, alerts and alarms) */
const CreateCalendarItemScreen = (
  props: RootStackScreenProps<'CreateCalendarItem'>
) => {
  const {
    navigation: { goBack },
    route: { params },
  } = props;

  const { defaults } = params || {};

  const create = useCreateCalendarItem();
  const profile = useCurrentUser();

  useUpdateScreenChatContext(screen_context);

  const [state, dispatch] = useCalendarItemForm({
    item: createEventModel({
      habitatID: profile.habitatID,
      principal: profile,
      participantIDs: [profile.id],
      emoji: defaults?.emoji || '📅',
      title: defaults?.title || '',
      start: defaults?.start ? new Date(defaults.start) : undefined,
      end: defaults?.end ? new Date(defaults.end) : undefined,
      color: defaults?.color,
      reminders: getDefaultReminders(params?.type),
    }),
    mode: 'create',
  });

  useUpdateCalendarItemFormListener(
    useCallback(
      (item) => {
        dispatch({ type: 'update_item', payload: { item } });
      },
      [dispatch]
    )
  );

  const onSave = useSaveCommand(
    useCallback(() => {
      if (!state.item) return;

      const check = validateCalendarItemFormSubmission({
        mode: 'edit',
        item: state.item,
      });

      if ('error' in check) {
        showErrorToast({
          text1: 'Submission Error',
          text2: check.error,
        });

        return;
      }

      showInfoToast({
        text1: 'Creating event...',
      });

      create(state.item).then((result) => {
        if (!result || !result.data) return;

        afterCreateCalendarItem(result.data);
      });

      goBack(); // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [profile.id, state.item])
  );

  useEffect(() => {
    sendBackgroundMessage(intent_labels.open_create_calendar_item_screen);
  }, []);

  return (
    <>
      <CalendarItemForm
        state={state}
        dispatch={dispatch}
        onPressBack={goBack}
        onPressSave={onSave}
      />

      <DeveloperScreenInfo
        context={<JSONText>{screen_context}</JSONText>}
        parameters={<JSONText>{parameters}</JSONText>}
      />
    </>
  );
};

export default CreateCalendarItemScreen;
